<template>
  <section class="editar-monto-buro">
    <Toolbar />
    <div class="page-shell">
      <section class="simulator-section">
        <div class="sim-title">Ajusta tu monto</div>
        <div class="sim-subtitle">
          Tu monto máximo aprobado es
          <strong>{{ montoEnteroMoneda(credito.monto.max) }}</strong>
        </div>

        <div class="slider-wrap">
          <v-slider
            v-model="montoInput"
            step="100"
            :min="montoMinimo"
            :max="credito.monto.max"
            color="#97c400"
            track-color="#d9d9d9"
            hide-details
            class="slider-monto"
            @end="endChange"
          />
          <div class="slider-labels">
            <span>{{ montoEnteroMoneda(montoMinimo) }}</span>
            <span>{{ montoEnteroMoneda(credito.monto.max) }}</span>
          </div>
        </div>

        <div class="monto-row">
          <button type="button" class="round-btn" @click="disminuirMonto">
            <v-icon color="white" size="20">mdi-minus-circle-outline</v-icon>
          </button>

          <div class="monto-box" @click="iniciarEdicionMonto">
            <input
              v-if="montoEditando"
              ref="montoInputRef"
              class="monto-box-value monto-box-input"
              type="text"
              inputmode="numeric"
              :value="montoInputTexto"
              @input="montoInputTexto = $event.target.value.replace(/\D/g, '')"
              @blur="confirmarEdicionMonto"
              @keyup.enter="$refs.montoInputRef.blur()"
            />
            <div v-else class="monto-box-value">
              {{ montoEnteroMoneda(montoInput) }}
            </div>
            <div class="monto-box-line"></div>
          </div>

          <button type="button" class="round-btn" @click="aumentarMonto">
            <v-icon color="white" size="20">mdi-plus-circle-outline</v-icon>
          </button>
        </div>

        <div
          v-if="esMontoInputInvalido"
          class="monto-alerta monto-alerta-error"
        >
          {{ montoInputError }}
        </div>

        <div class="plazo-title">Elige en cuantos meses quieres pagarlo</div>

        <div class="plazos-grid">
          <button
            v-for="plazo in plazosUnicos"
            :key="plazo.val"
            type="button"
            class="plazo-chip"
            :class="{ activo: plazoSeleccionado === plazo.val }"
            @click="plazoSeleccionado = plazo.val"
          >
            {{ plazo.val }}
          </button>
        </div>

        <div class="pago-title">
          Tu pago {{ credito.frecuenciaSeleccionada.val.toLowerCase() }} es:
        </div>

        <div class="pago-amount">
          <template v-if="!cargando">
            {{ montoMoneda(credito.montoPago.val) }}
          </template>
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
            :width="3"
            size="28"
          />
        </div>

        <div class="pago-note">* Sin penalizaciones por pagos anticipados</div>

        <div class="tasa-info">Tasa mensual: {{ credito.tasa.val }}%</div>

        <div class="cta-wrap">
          <v-btn
            rounded
            large
            color="#ff5a1f"
            class="cta-btn"
            depressed
            :disabled="
              esMontoInputInvalido ||
              credito.monto.val < montoMinimo ||
              credito.monto.val > credito.monto.max
            "
            :loading="cargando"
            @click="submit"
          >
            CONTINUAR
          </v-btn>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import Toolbar from '@/components/compartidos/Toolbar.vue'
import Credito from '@/servicios/credito'
import { mapActions } from 'vuex'
import { ALERTA_ERROR } from '@/utilidades/utilidades'
import { FormatoMonedaMixin } from '@/mixins/formateos/FormatoMoneda.mixin'
import { InsertaLogMixin } from '@/mixins/credito/InsertaLog.mixin'
import axios from 'axios'

export default {
  name: 'EditarMontoBuro',
  components: { Toolbar },
  mixins: [FormatoMonedaMixin, InsertaLogMixin],
  data: () => ({
    montoMinimo: 5000,
    credito: '',
    montoInput: 0,
    montoEditando: false,
    montoInputTexto: '',
    esMontoInputInvalido: false,
    montoInputError: '',
    cargando: false,
    plazoSeleccionado: 0,
    plazos: [],
  }),
  computed: {
    plazosUnicos() {
      const arreglo = Array.isArray(this.plazos) ? this.plazos : []
      const mapa = new Map()

      const tieneMultiplosDe6 = arreglo.some((item) => {
        const v = Number(item.val || item.plazo)
        return v > 0 && v % 6 === 0
      })

      arreglo.forEach((item) => {
        const valor = Number(item.val || item.plazo)
        const esMultiploValido = !tieneMultiplosDe6 || valor % 6 === 0
        if (valor > 0 && !mapa.has(valor) && esMultiploValido) {
          mapa.set(valor, { ...item, val: valor })
        }
      })

      return Array.from(mapa.values()).sort((a, b) => a.val - b.val)
    },
  },
  mounted() {
    this.InsertaLog()
  },
  created() {
    this.$store.commit('ESTABLECER_LAYOUT', 'Layout')
    this.credito = this.$store.getters['credito/obtenerCredito']
    this.montoInput = this.credito.monto.val
    this.plazoSeleccionado = this.credito.plazo.val
    this.plazos = this.$store.getters['credito/obtenerPlazos']
  },
  methods: {
    ...mapActions({
      enviarAlertaError: 'alerta/enviarAlertaError',
    }),
    montoEnteroMoneda(valor) {
      return Number(valor || 0).toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    },
    endChange() {
      this.credito.monto.val = this.montoInput
      this.updateMontoPago()
    },
    iniciarEdicionMonto() {
      this.montoInputTexto = String(this.montoInput)
      this.montoEditando = true
      this.$nextTick(() => {
        this.$refs.montoInputRef.select()
      })
    },
    confirmarEdicionMonto() {
      const raw = parseInt(this.montoInputTexto, 10)
      if (!isNaN(raw)) {
        const redondeado = Math.round(raw / 100) * 100
        const clampado = Math.min(
          Math.max(redondeado, this.montoMinimo),
          this.credito.monto.max
        )
        this.montoInput = clampado
        this.credito.monto.val = clampado
        this.updateMontoPago()
      }
      this.montoEditando = false
    },
    aumentarMonto() {
      const maximo = this.credito.monto.max
      const siguiente = this.montoInput + 100
      this.montoInput = siguiente > maximo ? maximo : siguiente
      this.credito.monto.val = this.montoInput
      this.updateMontoPago()
    },
    disminuirMonto() {
      const siguiente = this.montoInput - 100
      this.montoInput =
        siguiente < this.montoMinimo ? this.montoMinimo : siguiente
      this.credito.monto.val = this.montoInput
      this.updateMontoPago()
    },
    updateCredito() {
      this.credito.plazo = {
        mask: this.credito.plazo.mask,
        name: this.plazoSeleccionado + this.credito.plazo.mask,
        title: 'PLAZO',
        val: this.plazoSeleccionado,
      }
      this.$store.dispatch('credito/actualizarCredito', this.credito)
    },
    async updateMontoPago() {
      let result
      try {
        this.cargando = true
        result = await Credito.obtenerPagoSimulado(this.credito)
        if (result.data.Codigo == 200) {
          this.$store.dispatch(
            'credito/actuliazarMontoPago',
            result.data.Modelo
          )
        } else {
          this.enviarAlertaError(
            result.data.Mensaje,
            ALERTA_ERROR.tiempo,
            ALERTA_ERROR.visible
          )
        }
        this.cargando = false
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.enviarAlertaError(
            ALERTA_ERROR.mensaje,
            ALERTA_ERROR.tiempo,
            ALERTA_ERROR.visible
          )
          this.cargando = false
        }
      }
    },
    submit() {
      this.updateCredito()
      this.$router.push('/datosDomicilio')
    },
  },
  watch: {
    plazoSeleccionado() {
      this.updateCredito()
      this.updateMontoPago()
    },
    montoInput(value) {
      this.credito.monto.val = value
      if (value < this.montoMinimo) {
        this.esMontoInputInvalido = true
        this.montoInputError = 'El monto mínimo es de $' + this.montoMinimo
      } else if (value > this.credito.monto.max) {
        this.esMontoInputInvalido = true
        this.montoInputError =
          'El monto máximo es de $' + this.credito.monto.max
      } else if (value % 100 !== 0) {
        this.esMontoInputInvalido = true
        this.montoInputError = 'Elige un monto múltiplo de 100'
      } else {
        this.esMontoInputInvalido = false
        this.credito.monto.val = this.montoInput
        this.montoInputError = ''
      }
    },
  },
}
</script>

<style scoped>
.editar-monto-buro {
  background: #ffffff;
  min-height: 100vh;
}
.page-shell {
  background: #ffffff;
  min-height: 100vh;
}

.simulator-section {
  text-align: center;
  padding: 30px 24px 28px;
}

.sim-title {
  font-size: 24px;
  line-height: 28px;
  font-weight: 900;
  color: #222222;
  margin-bottom: 8px;
}

.sim-subtitle {
  font-size: 15px;
  line-height: 20px;
  color: #2a2a2a;
  margin-bottom: 22px;
}

.slider-wrap {
  max-width: 320px;
  margin: 0 auto 14px;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  line-height: 14px;
  color: #1f1f1f;
  font-weight: 800;
  margin-top: -2px;
}

.monto-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 18px;
  margin-bottom: 22px;
}

.round-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: #97c400;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.monto-box {
  width: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
}

.monto-box-value {
  font-size: 33px;
  line-height: 36px;
  font-weight: 900;
  color: #232a35;
  margin-bottom: 6px;
}

.monto-box-input {
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  width: 100%;
  padding: 0;
  cursor: text;
}

.monto-box-line {
  width: 100%;
  height: 2px;
  background: #7b7b7b;
  border-radius: 999px;
}

.monto-alerta {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.monto-alerta-error {
  color: #d32f2f;
}

.plazo-title {
  font-size: 17px;
  line-height: 22px;
  color: #2a2a2a;
  font-weight: 700;
  margin-bottom: 14px;
}

.plazos-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
}

.plazo-chip {
  height: 42px;
  min-width: 48px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #b7cf86;
  background: #f7f7f7;
  color: #5b5b5b;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
}

.plazo-chip.activo {
  background: #97c400;
  border-color: #97c400;
  color: white;
}

.pago-title {
  font-size: 17px;
  line-height: 20px;
  color: #2a2a2a;
  font-weight: 800;
  margin-bottom: 4px;
}

.pago-amount {
  font-size: 33px;
  line-height: 36px;
  color: #ff5a1f;
  font-weight: 900;
  margin-bottom: 6px;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.08);
}

.pago-note {
  font-size: 11px;
  line-height: 15px;
  color: #2f2f2f;
  margin-bottom: 22px;
}

.tasa-info {
  font-size: 13px;
  line-height: 16px;
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 18px;
}

.cta-wrap {
  display: flex;
  justify-content: center;
}

.cta-btn {
  width: 100%;
  max-width: 300px;
  text-transform: none !important;
  font-size: 20px !important;
  font-weight: 900 !important;
  letter-spacing: 0 !important;
  color: white !important;
  height: 56px !important;
  box-shadow: none !important;
  margin-bottom: 10px;
}

::v-deep .slider-monto .v-slider__track-background {
  height: 6px !important;
  border-radius: 999px;
}

::v-deep .slider-monto .v-slider__track-fill {
  height: 6px !important;
  border-radius: 999px;
}

::v-deep .slider-monto .v-slider__thumb {
  width: 20px !important;
  height: 20px !important;
  left: -10px !important;
  box-shadow: none !important;
}

::v-deep .slider-monto .v-slider__thumb::before {
  display: none !important;
}
</style>
